/**
 * The credentials page's organizations rail, as `MainLayout` mounts it.
 *
 * It sat inside the page first, and so it stopped where the list stopped: a
 * short estate left a stub of a column beside an empty surface. Mounted as a
 * sibling of `<main>`, beside Desk's rail, it runs the full height as Desk's
 * does, and it renders nothing on any other route.
 *
 * The tree comes from the page (see `railStore`), so the rail costs no
 * request of its own. The selection comes from the scope, the same answer the
 * scope bar shows, so choosing an organization here moves the scope and the
 * page follows it.
 */
import { useLocation } from "react-router-dom";

import { useScopeHierarchy } from "@/hooks/useScopeHierarchy";
import { useAuthStore } from "@/store/authStore";

import { groupAxisForRole } from "./credentialHealth";
import { OrgNavigator, OrgNavigatorSkeleton } from "./OrgNavigator";
import { applySelection, selectionFromScope } from "./orgTree";
import { useCredentialsRailStore } from "./railStore";

/** The page's route. A trailing slash is the same page. */
function isCredentialsPath(pathname: string): boolean {
  return pathname.replace(/\/+$/, "") === "/credentials";
}

export function CredentialsRail() {
  const { pathname } = useLocation();
  const role = useAuthStore((s) => s.user?.role);
  // A company viewer owns one company, and a rail with a single leaf would
  // only restate the scope bar. Known from the role before any data, so the
  // column is either there from the first frame or never.
  if (!isCredentialsPath(pathname) || groupAxisForRole(role) === "none") {
    return null;
  }
  return <CredentialsRailBody />;
}

/**
 * Split from the route check so the scope hook runs only on the page that
 * uses it, not on every route the layout renders.
 */
function CredentialsRailBody() {
  const snapshot = useCredentialsRailStore((s) => s.snapshot);
  const scope = useScopeHierarchy();

  // Nothing published yet (the page's code is still loading), or still
  // loading with nothing real to show: the skeleton holds the column, so the
  // list does not slide sideways when the tree arrives. A tree built before
  // the rows would show a zero beside every organization, and a zero is a
  // claim, not a waiting state.
  if (
    !snapshot ||
    (snapshot.loading && (snapshot.total === 0 || snapshot.tree.length === 0))
  ) {
    return <OrgNavigatorSkeleton />;
  }
  if (!snapshot.listing || snapshot.tree.length === 0) return null;

  return (
    <OrgNavigator
      tree={snapshot.tree}
      selection={selectionFromScope(scope)}
      onSelect={(next) => applySelection(scope, next)}
      total={snapshot.total}
      totalAttention={snapshot.totalAttention}
      bandsSelectable={!scope.isMSPAdmin}
    />
  );
}
